/**
 * @module tokenizer
 * Provides a function to fetch tokenized representations of input text
 * from a backend API endpoint. The backend should return an array of tokens,
 * each with an id and text property.
 */

/**
 * Fetches tokens from the backend API for a given input text.
 *
 * This function sends a POST request to the backend endpoint at
 * https://node-demo-red.vercel.app/api/getTokensFromText with the input text
 * and a specified language model ("gpt-4o"). The backend is expected to return
 * a JSON object containing a "tokens" array, where each token is an object
 * with "id" (number) and "text" (string) properties.
 *
 * @param {string} textInput - Raw input text to be tokenized.
 * @returns {Promise<Array<{id:number,text:string}>>} - Resolves to an array of token objects.
 * @throws {Error} If the API response is not OK or the network request fails.
 *
 * Example usage:
 *   const tokens = await fetchTokensFromText("Hello world!");
 *   // tokens: [{id: 123, text: "Hello"}, {id: 456, text: "world"}, ...]
 */
export async function fetchTokensFromText(textInput) {
  //test the input
  if (typeof textInput !== "string") {
    throw new Error(
      "[tokenizer.js] fetchTokensFromText: input text must be a string"
    );
  }
  if (textInput.length === 0) {
    throw new Error(
      "[tokenizer.js] fetchTokensFromText: input text must not be empty"
    );
  }
  // Debug: log the input text..only show the first few words
  const previewText = textInput.split(" ").slice(0, 10).join(" ");
  console.debug("[tokenizer.js] fetchTokensFromText: input text", previewText);
  // Send a POST request to the backend API with the input text and model name
  const res = await fetch(
    "/api/getTokensFromText",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ language_model: "gpt-4o", text: textInput }),
    }
  );

  // Debug: log the response status
  console.debug(
    "[tokenizer.js] fetchTokensFromText: response status",
    res.status
  );

  // If the response is not successful, throw an error with context
  if (!res.ok) {
    throw new Error(
      `[tokenizer.js] fetchTokensFromText: Token API error (status ${res.status})`
    );
  }

  // Parse the JSON response and extract the tokens array
  const { tokens } = await res.json();
  return tokens;
}
